import { defineMessages, type MessageDescriptor } from 'react-intl'
import { getImageUrl } from 'lib/image/imageUtils'
import Places from 'constants/places'

export type ThereNoPlaceLikeHomePopupMarket = 'nsw' | 'vic'
export type ThereNoPlaceLikeHomePopupVariant = 'control' | 'variant'

export type ThereNoPlaceLikeHomePopupActionId =
  | 'firstPrompt'
  | 'secondPrompt'
  | 'thirdPrompt'
  | 'tryAiTripPlanner'
  | 'exploreNswEscapes'
  | 'exploreVicEscapes'

interface ThereNoPlaceLikeHomePopupPromptConfig {
  actionId: ThereNoPlaceLikeHomePopupActionId
  message: MessageDescriptor
  promptMessage?: MessageDescriptor
  contextMessage?: string
  contextLabel?: string
}

interface ThereNoPlaceLikeHomePopupActionConfig {
  actionId: ThereNoPlaceLikeHomePopupActionId
  message: MessageDescriptor
  promptMessage?: MessageDescriptor
  contextMessage?: string
  contextLabel?: string
}

interface ThereNoPlaceLikeHomePopupPathDestination {
  type: 'path'
  path: string
  search?: string
}

interface ThereNoPlaceLikeHomePopupPromptDestination {
  type: 'chatPrompt'
}

export type ThereNoPlaceLikeHomePopupActionDestination =
  | ThereNoPlaceLikeHomePopupPathDestination
  | ThereNoPlaceLikeHomePopupPromptDestination

type ThereNoPlaceLikeHomePopupActionConfigMap = Partial<Record<ThereNoPlaceLikeHomePopupActionId, ThereNoPlaceLikeHomePopupActionDestination>>

interface ThereNoPlaceLikeHomePopupImage {
  mobile: {
    width: number
    height: number
    url: string
  }
  desktop: {
    width: number
    height: number
    url: string
  }
}

interface ThereNoPlaceLikeHomePopupVariantConfig {
  label?: MessageDescriptor
  imageAlt: MessageDescriptor
  description: MessageDescriptor
  prompts: ReadonlyArray<ThereNoPlaceLikeHomePopupPromptConfig>
  primaryAction: ThereNoPlaceLikeHomePopupActionConfig
  actions: ThereNoPlaceLikeHomePopupActionConfigMap
}

const THERE_NO_PLACE_LIKE_HOME_POPUP_IMAGE_ID = 'trip-planner-popup'
const VIC_THERE_NO_PLACE_LIKE_HOME_POPUP_IMAGE_ID = 'a9uvcw4ly9jx2vzbyd6q'

function buildThereNoPlaceLikeHomePopupImage(imageId: string): ThereNoPlaceLikeHomePopupImage {
  return {
    mobile: {
      width: 375,
      height: 150,
      url: getImageUrl(imageId, {
        width: 750,
        height: 300,
        quality: 'best',
      }),
    },
    desktop: {
      width: 720,
      height: 200,
      url: getImageUrl(imageId, {
        width: 1440,
        height: 400,
        quality: 'best',
      }),
    },
  }
}

export const thereNoPlaceLikeHomePopupImages: Record<ThereNoPlaceLikeHomePopupMarket, ThereNoPlaceLikeHomePopupImage> = {
  nsw: buildThereNoPlaceLikeHomePopupImage(THERE_NO_PLACE_LIKE_HOME_POPUP_IMAGE_ID),
  vic: buildThereNoPlaceLikeHomePopupImage(VIC_THERE_NO_PLACE_LIKE_HOME_POPUP_IMAGE_ID),
}

export const thereNoPlaceLikeHomePopupMessages = defineMessages({
  imageAltNsw: {
    id: 'X-J2mj0oSM2KL8KdIPEZ',
    defaultMessage: 'Luxury home with a swimming pool in regional New South Wales',
    description: "Accessibility text for the There's No Place Like Home popup hero image showing a luxury home in New South Wales",
  },
  imageAltVic: {
    id: '3ZPCM9HnAzMrLe4_SMBp',
    defaultMessage: 'Luxury home with a swimming pool in regional Victoria',
    description: "Accessibility text for the There's No Place Like Home popup hero image showing a luxury home in Victoria",
  },
  label: {
    id: '5nrz2l--F1jQorfaNTGl',
    defaultMessage: 'Explore with AI',
    description: 'Short label for the AI Trip Planner feature shown inside the promotional homepage popup',
  },
  title: {
    id: 'eRRiuoNPO1R0D7UPaAgQ',
    defaultMessage: 'Not sure where to go this weekend?',
    description: 'Headline in the homepage popup prompting members to try AI Trip Planner for weekend travel',
  },
  descriptionNsw: {
    id: 'XHmIfWccSSw5CdPQW2zZ',
    defaultMessage: 'Ask AI to discover the perfect luxury escape in NSW, from wine country to coastal retreats.',
    description: 'Description in the homepage popup explaining the AI Trip Planner weekend escape use case',
  },
  descriptionVic: {
    id: '0eNVlN7l-5AhqKDUgSs3',
    defaultMessage: 'Ask AI to discover the perfect luxury escape in Victoria, from wine country to coastal retreats.',
    description: 'Description in the homepage popup explaining the AI Trip Planner weekend escape use case for Victoria',
  },
  descriptionNswNls: {
    id: 'Eovb0XUUDfjlLPo41nw6',
    defaultMessage: 'Discover the perfect luxury escape in NSW, from wine country to coastal retreats.',
    description: 'Description in the homepage popup explaining the weekend escape use case for New South Wales',
  },
  descriptionVicNls: {
    id: 'EvjANAvGqOhPJeF4iWba',
    defaultMessage: 'Discover the perfect luxury escape in Victoria, from wine country to coastal retreats.',
    description: 'Description in the homepage popup explaining the weekend escape use case for Victoria',
  },
  exploreNswEscapes: {
    id: '7USJHDETCHaoFE3fz9wH',
    defaultMessage: 'Explore NSW escapes',
    description: "Secondary call to action in the There's No Place Like Home popup for browsing New South Wales escapes",
  },
  exploreVicEscapes: {
    id: 'LBUJdyKnEPfEvh7SgNa5',
    defaultMessage: 'Explore VIC escapes',
    description: "Secondary call to action in the There's No Place Like Home popup for browsing Victorian escapes",
  },
  tryAiTripPlanner: {
    id: 'Wo7p1Id8sO1nu7_59scB',
    defaultMessage: 'Explore with AI',
    description: "Primary call to action in the There's No Place Like Home popup for opening the AI TripPlanner experience",
  },
  exploreWithAiNswPrompt: {
    id: 'w6MwetNAZz5G-1KBXM4Y',
    defaultMessage: 'Help me find a weekend getaway in NSW',
    description: 'Prefilled Trip Planner prompt for starting a generic New South Wales weekend escape chat',
  },
  exploreWithAiVicPrompt: {
    id: 'Xyb5gYhHQ60AtX1oeCXu',
    defaultMessage: 'Help me find a weekend getaway in Victoria',
    description: 'Prefilled Trip Planner prompt for starting a generic Victoria weekend escape chat',
  },
  close: {
    id: 'WwQdmQe_yv-M8RKLaxDI',
    defaultMessage: 'Close',
    description: 'Accessible aria-label for closing the Trip Planner promotional homepage popup',
  },
  remindMeNextTime: {
    id: 'p9NXASY55M3nPrz1qem0',
    defaultMessage: 'Remind me next time',
    description: "Tertiary button text in the There's No Place Like Home popup for snoozing the AI TripPlanner variant until the next browser session",
  },
})

const THERE_NO_PLACE_LIKE_HOME_POPUP_CONTEXT_LABEL = "There's No Place Like Home"

const thereNoPlaceLikeHomePopupCampaignContext = `Campaign: There's No Place Like Home.
This chat was started from the Luxury Escapes homepage popup for a limited-time domestic Australia campaign. The visible user message was prefilled by Luxury Escapes after the customer clicked a campaign prompt, so treat it as their starting intent but do not imply they manually typed it.

Campaign positioning:
- Discover the best of Australia.
- The customer's next escape is closer than they think.
- The campaign is about handpicked Australian stays for long weekends, family getaways and well-earned breaks.
- This popup is shown to Australian customers based on their geolocated state. Treat the state-specific prompt as a local, home-state escape suggestion rather than an interstate or overseas travel prompt.

First-response guidance:
- Instantly deliver value in the first reply.
- Mention the There's No Place Like Home campaign naturally and briefly.
- Position AI TripPlanner as a conversational travel-planning experience. Keep it natural: the customer can ask follow-up questions, compare ideas, check availability, search offers, explore hotels, flights and things to do, and shape the trip as they go.
- Do not call search_hotels for this first reply. Use the campaign offer context below for the initial response; you are free to call it on any other turn!
- Show relevant hotel offers in a carousel or offer-card UI when available. Prefer the campaign offer IDs listed below.
- Mention the supplied lead prices for the selected offers when presenting them; these prices are a key part of why the campaign offers are good deals.
- Include a clear interactive next-step UI with options the customer can pick from.
- If the visible prompt names a destination, prioritise that destination first, then suggest nearby campaign options where useful.`

const nswThereNoPlaceLikeHomePopupContext = `${thereNoPlaceLikeHomePopupCampaignContext}

NSW campaign offers to prioritise:
- Sydney / Pyrmont | 006RF00000QLzmHYAT | The Darling | Sydney Harbourside Glamour: Pyrmont Luxury with Daily Breakfast, Daily A$50 Dining Credit & Parking | From A$679 with LuxPlus+ (A$699 non-member)
- Sydney | 006RF00000VTXunYAH | Ace Hotel Sydney | Award-Winning Sydney Luxury with Rooftop Restaurant, Daily Breakfast & Nightly Drinks | From A$529
- Potts Point | 006RF00000VJ4xKYAT | Hotel Indigo Sydney Potts Point, an IHG Hotel | Art-Inspired Sydney Glamour: Potts Point Escape near Surry Hills with Daily Breakfast | From A$379 with LuxPlus+ (A$399 non-member)
- Pokolbin | 006RF00000TWd2MYAT | Chateau Elan at The Vintage | Hunter Valley Tuscany-Inspired Escape with Daily Breakfast & Cellar Door Wine Tastings | From A$429 with LuxPlus+ (A$449 non-member)
- Lovedale | 006RF00000UPFqeYAH | Wandin Valley Estate | Tuscan-Inspired Hunter Valley Vineyard Hideaway with Breakfast Hamper & Premium Wine Tasting | From A$439 with LuxPlus+ (A$459 non-member)
- Pokolbin | 006RF00000TzhAbYAJ | voco Kirkton Park Hunter Valley by IHG | Elegant Hunter Valley Estate with Daily Breakfast, Nightly Cocktails & Local Wine Tastings | From A$449 with LuxPlus+ (A$469 non-member)
- Medlow Bath | 006RF00000VZA7UYAX | The Hydro Majestic Hotel | Iconic Blue Mountains Luxury with Daily Sparkling Wine Breakfast, Two-Course Dinner & Nightly Cocktails | From A$569 with LuxPlus+ (A$599 non-member)
- Mollymook Beach | 006RF00000UZz2XYAT | Bannisters by the Sea | South Coast Haven: Boutique Mollymook Escape with Daily Breakfast & Rick Stein Dining Upgrades Available | From A$749 with LuxPlus+ (A$799 non-member)
- Killcare Heights | 006RF00000SQPPIYA5 | Bells at Killcare Boutique Hotel, Restaurant & Spa | NSW Central Coast Luxury Lodges & Boutique Villas with Daily Breakfast & A$100 Resort Credit | From A$839 with LuxPlus+ (A$879 non-member)
- Ettalong Beach | 006RF00000TEfozYAD | Mantra Ettalong Beach | Central Coast Waterfront Bliss: One- & Two-Bedroom Suites with Oyster Tour Upgrades Available | From A$289 with LuxPlus+ (A$299 non-member)
- Tumbi Umbi | 006RF00000TGVLiYAP | Encore by Mingara | Modern Central Coast Escape Just 90 Minutes From Sydney near Tuggerah Lake with Daily Breakfast | From A$339 with LuxPlus+ (A$359 non-member)`

const vicThereNoPlaceLikeHomePopupContext = `${thereNoPlaceLikeHomePopupCampaignContext}

VIC campaign offers to prioritise:
- South Wharf | 006RF00000UXG1XYAX | Pan Pacific Melbourne | Award-Winning Melbourne Riverfront Luxury with Daily Breakfast & Club Lounge Upgrades Available | From A$479 with LuxPlus+ (A$499 non-member)
- Melbourne CBD | 006RF00000UzK2CYAV | Pullman Melbourne City Centre | Melbourne CBD Glamour with Rooftop Bar, Daily Breakfast, Nightly Drinks & Parking | From A$429 with LuxPlus+ (A$449 non-member)
- Melbourne / Flinders Lane | 006RF00000V56CbYAJ | Hotel Indigo Melbourne on Flinders, an IHG Hotel | Art-Inspired Melbourne Flinders Lane Escape near Yarra River with Daily Breakfast | From A$379 with LuxPlus+ (A$399 non-member)
- Melbourne CBD | 006RF00000WDNu6YAH | Adina Apartment Hotel Melbourne | Melbourne CBD One-, Two- & Three-Bedroom Apartments with Indoor Pool & Complimentary Parking | From A$379 with LuxPlus+ (A$399 non-member)
- South Yarra | 006RF00000TZAewYAH | Ovolo Melbourne, South Yarra | Melbourne Retro-Inspired Glamour on Chapel Street with Daily Breakfast & Nightly Drinks | From A$379 with LuxPlus+ (A$399 non-member)
- Melbourne CBD | 006RF00000XJ6jRYAT | Rendezvous Hotel Melbourne | Melbourne Heritage Escape near Yarra River with Daily Breakfast & Nightly Drink | From A$379 with LuxPlus+ (A$399 non-member)
- Sorrento | 006RF00000RinrtYAB | Hotel Sorrento | Mornington Peninsula Adults-Only Clifftop Glamour with Daily Breakfast & A$100 Dining Credit | From A$669 with LuxPlus+ (A$699 non-member)
- Cowes | 006RF00000U75cnYAB | Ramada Resort by Wyndham Phillip Island | Phillip Island Villas with Daily Breakfast, Pizza Dinner & Penguin Tour Upgrades Available | From A$429 with LuxPlus+ (A$449 non-member)
- Lancefield | 006RF00000RiTuoYAF | Cleveland Estate | Macedon Ranges Scenic Vineyard Hideaway with Daily Breakfast & Boutique Beer Tastings | From A$479 with LuxPlus+ (A$499 non-member)
- Mickleham | 006RF00000SQmX8YAL | Marnong Estate | Central Victoria Vineyard Studio & Apartment Luxury with Wine Tasting, Dining Credit & Daily Breakfast | From A$519 with LuxPlus+ (A$549 non-member)
- Geelong | 006RF00000Kgw45YAB | Bayside Geelong Hotel & Apartments | Geelong Bayfront Boutique Stay with Daily Breakfast, Nightly Drinks, Parking & A$50 Dining Credit | From A$379 with LuxPlus+ (A$399 non-member)
- Barwon Heads | 006RF00000VayqJYAR | 13th Beach Golf Lodges | Barwon Heads Golf Retreat 90-Minutes from Melbourne with Championship Course, Breakfast & Nightly Drinks | From A$379 with LuxPlus+ (A$399 non-member)
- Torquay | 006RF00000MTKebYAH | Wyndham Resort Torquay | Great Ocean Road Coastal Torquay Escape with Outdoor Pool & Apartment Upgrades Available | From A$309 with LuxPlus+ (A$329 non-member)
- Whitfield | 006RF00000UMX2WYAX | Dal Zotto Homestead and Petite Retreats | Victoria High Country Italian-Inspired Retreat with Wine Tasting & Daily Breakfast Hamper | From A$429 with LuxPlus+ (A$449 non-member)
- Willaura | 006RF00000U4PxpYAF | Mount William Station | Award-Winning Grampians Homestead with Daily Breakfast, Nightly Three-Course Dinner & Wine Tasting | From A$949 with LuxPlus+ (A$999 non-member)
- Creswick | 006RF00000SgM8rYAF | The Sebel Creswick Forest Resort | Charming Self-Contained Two-Bedroom Apartments near Daylesford & Hepburn Springs | From A$529 with LuxPlus+ (A$549 non-member)
- Yarrawonga | 006RF00000Kkq6YYAR | The Sebel Yarrawonga Silverwoods | Victoria Lakeside Escape near Black Bull Golf Course with Daily Breakfast, Round of Golf & $50 Dining Credit | From A$769 with LuxPlus+ (A$799 non-member)`

const thereNoPlaceLikeHomePopupPromptMessages = defineMessages({
  hunterValley: {
    id: 'JTly7FIjXcMBXBLO99Wn',
    defaultMessage: 'Sip your way through the Hunter Valley',
    description: "Prompt chip text in the There's No Place Like Home popup for planning a weekend in Hunter Valley",
  },
  blueMountains: {
    id: '-hZdX4-fXO1B10qvCn2g',
    defaultMessage: 'Plan a luxury Sydney city break',
    description: "Prompt chip text in the There's No Place Like Home popup for planning a luxury Sydney city break",
  },
  mollymook: {
    id: 'v4tNbT52fTiljeEW0tXp',
    defaultMessage: 'Escape to the NSW coast',
    description: "Prompt chip text in the There's No Place Like Home popup for planning a weekend in Mollymook",
  },
  morningtonPeninsula: {
    id: 'mj4ouW0MBwv9G0v3HMCe',
    defaultMessage: 'Unwind on the Mornington Peninsula',
    description: "Prompt chip text in the There's No Place Like Home popup for planning a weekend in Mornington Peninsula",
  },
  greatOceanRoad: {
    id: 'yIxdYwr-C2MYhjeX0t9Z',
    defaultMessage: 'Road trip the Great Ocean Road',
    description: "Prompt chip text in the There's No Place Like Home popup for planning a weekend on the Great Ocean Road",
  },
  macedonRanges: {
    id: '7mtvnr8gmMa4dEoeDeM0',
    defaultMessage: 'Retreat to the Macedon Ranges',
    description: "Prompt chip text in the There's No Place Like Home popup for planning a weekend in the Macedon Ranges",
  },
})

export const thereNoPlaceLikeHomePopupNswPrompts: ReadonlyArray<ThereNoPlaceLikeHomePopupPromptConfig> = [
  {
    actionId: 'firstPrompt',
    message: thereNoPlaceLikeHomePopupPromptMessages.hunterValley,
    contextMessage: nswThereNoPlaceLikeHomePopupContext,
    contextLabel: THERE_NO_PLACE_LIKE_HOME_POPUP_CONTEXT_LABEL,
  },
  {
    actionId: 'secondPrompt',
    message: thereNoPlaceLikeHomePopupPromptMessages.blueMountains,
    contextMessage: nswThereNoPlaceLikeHomePopupContext,
    contextLabel: THERE_NO_PLACE_LIKE_HOME_POPUP_CONTEXT_LABEL,
  },
  {
    actionId: 'thirdPrompt',
    message: thereNoPlaceLikeHomePopupPromptMessages.mollymook,
    contextMessage: nswThereNoPlaceLikeHomePopupContext,
    contextLabel: THERE_NO_PLACE_LIKE_HOME_POPUP_CONTEXT_LABEL,
  },
]

export const thereNoPlaceLikeHomePopupVicPrompts: ReadonlyArray<ThereNoPlaceLikeHomePopupPromptConfig> = [
  {
    actionId: 'firstPrompt',
    message: thereNoPlaceLikeHomePopupPromptMessages.morningtonPeninsula,
    contextMessage: vicThereNoPlaceLikeHomePopupContext,
    contextLabel: THERE_NO_PLACE_LIKE_HOME_POPUP_CONTEXT_LABEL,
  },
  {
    actionId: 'secondPrompt',
    message: thereNoPlaceLikeHomePopupPromptMessages.greatOceanRoad,
    contextMessage: vicThereNoPlaceLikeHomePopupContext,
    contextLabel: THERE_NO_PLACE_LIKE_HOME_POPUP_CONTEXT_LABEL,
  },
  {
    actionId: 'thirdPrompt',
    message: thereNoPlaceLikeHomePopupPromptMessages.macedonRanges,
    contextMessage: vicThereNoPlaceLikeHomePopupContext,
    contextLabel: THERE_NO_PLACE_LIKE_HOME_POPUP_CONTEXT_LABEL,
  },
]

export const thereNoPlaceLikeHomePopupNswPrimaryAction: ThereNoPlaceLikeHomePopupActionConfig = {
  actionId: 'tryAiTripPlanner',
  message: thereNoPlaceLikeHomePopupMessages.tryAiTripPlanner,
  promptMessage: thereNoPlaceLikeHomePopupMessages.exploreWithAiNswPrompt,
  contextMessage: nswThereNoPlaceLikeHomePopupContext,
  contextLabel: THERE_NO_PLACE_LIKE_HOME_POPUP_CONTEXT_LABEL,
}

export const thereNoPlaceLikeHomePopupVicPrimaryAction: ThereNoPlaceLikeHomePopupActionConfig = {
  actionId: 'tryAiTripPlanner',
  message: thereNoPlaceLikeHomePopupMessages.tryAiTripPlanner,
  promptMessage: thereNoPlaceLikeHomePopupMessages.exploreWithAiVicPrompt,
  contextMessage: vicThereNoPlaceLikeHomePopupContext,
  contextLabel: THERE_NO_PLACE_LIKE_HOME_POPUP_CONTEXT_LABEL,
}

export const thereNoPlaceLikeHomePopupNswExploreAction: ThereNoPlaceLikeHomePopupActionConfig = {
  actionId: 'exploreNswEscapes',
  message: thereNoPlaceLikeHomePopupMessages.exploreNswEscapes,
}

export const thereNoPlaceLikeHomePopupVicExploreAction: ThereNoPlaceLikeHomePopupActionConfig = {
  actionId: 'exploreVicEscapes',
  message: thereNoPlaceLikeHomePopupMessages.exploreVicEscapes,
}

function buildDestinationSearch(destinationName: string, destinationId: string): string {
  return new URLSearchParams({
    destinationName,
    destinationId,
    adults: '2',
    children: 'none',
  }).toString()
}

const nswSearchDestination: ThereNoPlaceLikeHomePopupPathDestination = {
  type: 'path',
  path: '/search',
  search: buildDestinationSearch(Places.NewSouthWales.name, Places.NewSouthWales.id),
}

const vicSearchDestination: ThereNoPlaceLikeHomePopupPathDestination = {
  type: 'path',
  path: '/search',
  search: buildDestinationSearch(Places.Victoria.name, Places.Victoria.id),
}

const nswNlsSearchDestination: ThereNoPlaceLikeHomePopupPathDestination = {
  type: 'path',
  path: '/search/map',
  search: 'destinationId=le_4371d2566cbf4eb16e1cda1ce268c63c%2Cle_4c6515356f214aa275e07ddbbd922a5e%2Cle_864d8b8a2daebb7269e0882b0bfb6232%2Cle_8ebf84a4954068cc2a998f04fb26fea6%2Cle_b26457e8774dcf3bf66024f7600ee686%2Cle_3d0e57482f96c68518495781355abab0%2Cle_c256df8aa62d7d94ff1a7c4ecf70245f%2Cle_5186b5eb83798a2a7a0bec02e7e95c5d%2Cle_2dddca165ef38c731e7cce6069107fa6%2Cle_2a15efb072c4176909a1421563524eec&query=Weekend%20Escape%20near%20Sydney&adults=2&children=none&typeaheadId=9f1e6ac4-b529-4ed4-8b63-8121a6b092be&offerTypes=hotel%2Ctactical_ao_hotel%2Cbedbank_hotel',
}

const vicNlsSearchDestination: ThereNoPlaceLikeHomePopupPathDestination = {
  type: 'path',
  path: '/search/map',
  search: 'offerTypes=hotel%2Ctactical_ao_hotel%2Cbedbank_hotel%2Crental&destinationId=le_afcf8ed19482ebcf0c9b20bde2b56b7b%2Cle_dd5bfdeb57f7c75d400de61e99d78e2e%2Cle_c5f441cd5f43eb2f2c024e1f8b5d00cd%2Cle_4e8a0fcae374fd3909f64555cdea95a1%2Cle_77a4a0e5a3bdfee80c9eff1420165977%2Cle_570a90174cbbe07bda17a54f94f47637%2Cle_e282d1cf59bc440f418e92b618e37e14%2Cle_8e809411bbd25cac078ba595cc2922f8%2Cle_558312eadb08b0c70b6e0f78b5e75a13%2Cle_994f13dc1e844f1682b23ddedd6761d6%2Cle_c94fbd073e07f7f8e207a302010e37f9%2Cle_b870141093da4f85dad61d23034910af%2Cle_a523426cc585745318d5f6d91a9c0706%2Cle_0dbf135f25773b9d43170343f492572f%2Cle_cc97a8205535cd8038f5b2384b2e42ae%2Cle_17c884551e0b6e764de15e72e2fa2e33%2Cle_cbca1b9d23a6cab35e2307032afb4ffc%2Cle_f643dd6acccb3debf42f4c6df805c67e%2Cle_3bb999121b9cfe9da181aa3d28078e3d%2Cle_f9a0324d9edfbcbc619040ddb94a4a61%2Cle_d0c55943093636296ede7c1ee125928c%2Cle_2fdbfd5cc2ea0b97e6b79eb5ca7ba215%2Cle_0f33d9fe6955f349a9145dfb2203f0e4&query=Weekend+Escape+near+Melbourne&adults=2&children=none&typeaheadId=83d04c97-fe32-4d8b-945d-33bf66d55c42',
}

const controlActionDestinationsByMarket: Record<ThereNoPlaceLikeHomePopupMarket, ThereNoPlaceLikeHomePopupActionConfigMap> = {
  nsw: {
    firstPrompt: { type: 'chatPrompt' },
    secondPrompt: { type: 'chatPrompt' },
    thirdPrompt: { type: 'chatPrompt' },
    tryAiTripPlanner: { type: 'chatPrompt' },
    exploreNswEscapes: nswSearchDestination,
  },
  vic: {
    firstPrompt: { type: 'chatPrompt' },
    secondPrompt: { type: 'chatPrompt' },
    thirdPrompt: { type: 'chatPrompt' },
    tryAiTripPlanner: { type: 'chatPrompt' },
    exploreVicEscapes: vicSearchDestination,
  },
}

const nswNlsActionDestinations: ThereNoPlaceLikeHomePopupActionConfigMap = {
  ...controlActionDestinationsByMarket.nsw,
  exploreNswEscapes: nswNlsSearchDestination,
}

const vicNlsActionDestinations: ThereNoPlaceLikeHomePopupActionConfigMap = {
  ...controlActionDestinationsByMarket.vic,
  exploreVicEscapes: vicNlsSearchDestination,
}

export const thereNoPlaceLikeHomePopupVariantConfig: Record<ThereNoPlaceLikeHomePopupMarket, Record<ThereNoPlaceLikeHomePopupVariant, ThereNoPlaceLikeHomePopupVariantConfig>> = {
  nsw: {
    control: {
      label: thereNoPlaceLikeHomePopupMessages.label,
      imageAlt: thereNoPlaceLikeHomePopupMessages.imageAltNsw,
      description: thereNoPlaceLikeHomePopupMessages.descriptionNsw,
      prompts: thereNoPlaceLikeHomePopupNswPrompts,
      primaryAction: thereNoPlaceLikeHomePopupNswPrimaryAction,
      actions: controlActionDestinationsByMarket.nsw,
    },
    variant: {
      imageAlt: thereNoPlaceLikeHomePopupMessages.imageAltNsw,
      description: thereNoPlaceLikeHomePopupMessages.descriptionNswNls,
      prompts: [],
      primaryAction: thereNoPlaceLikeHomePopupNswExploreAction,
      actions: nswNlsActionDestinations,
    },
  },
  vic: {
    control: {
      label: thereNoPlaceLikeHomePopupMessages.label,
      imageAlt: thereNoPlaceLikeHomePopupMessages.imageAltVic,
      description: thereNoPlaceLikeHomePopupMessages.descriptionVic,
      prompts: thereNoPlaceLikeHomePopupVicPrompts,
      primaryAction: thereNoPlaceLikeHomePopupVicPrimaryAction,
      actions: controlActionDestinationsByMarket.vic,
    },
    variant: {
      imageAlt: thereNoPlaceLikeHomePopupMessages.imageAltVic,
      description: thereNoPlaceLikeHomePopupMessages.descriptionVicNls,
      prompts: [],
      primaryAction: thereNoPlaceLikeHomePopupVicExploreAction,
      actions: vicNlsActionDestinations,
    },
  },
}

export function getThereNoPlaceLikeHomePopupMarket(currentRegionCode: string, geoStateName: string | undefined): ThereNoPlaceLikeHomePopupMarket | undefined {
  if (currentRegionCode !== 'AU') {
    return undefined
  }

  const normalisedGeoStateName = geoStateName?.trim().toLowerCase()

  if (normalisedGeoStateName === 'nsw' || normalisedGeoStateName === 'new south wales') {
    return 'nsw'
  }

  if (normalisedGeoStateName === 'vic' || normalisedGeoStateName === 'victoria') {
    return 'vic'
  }

  return undefined
}

export function getThereNoPlaceLikeHomePopupVariant(variation: string | undefined): ThereNoPlaceLikeHomePopupVariant | undefined {
  if (variation === 'control' || variation === 'variant') {
    return variation
  }

  return undefined
}
